import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withViewTransitions } from '@angular/router';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { registerLocaleData } from '@angular/common';
import localeSrLatn from '@angular/common/locales/sr-Latn';
import localeSr from '@angular/common/locales/sr';
import localeEnUS from '@angular/common/locales/en';

import { LOCALE_ID, isDevMode } from '@angular/core';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';

import { BrowserModule } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { routes } from './app.routes';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { jwtInterceptor } from './core/interceptors/jwt.interceptor';
import { provideApollo } from './graphql/apollo.provider';
import { SocialLoginModule } from '@abacritt/angularx-social-login';
import { GOOGLE_SIGN_IN_PROVIDER } from './authentication/utils/third-party-auth';
import { provideServiceWorker } from '@angular/service-worker';
import { AppUpdateService } from './core/services/app-update.service';
import { environment } from '../environments/environment';

// Register locale data for all supported locales
registerLocaleData(localeSr, 'sr');
registerLocaleData(localeSrLatn, 'sr-Latn');
registerLocaleData(localeEnUS, 'en-US');

const locale = environment.locale;

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: locale.dateFormat,
  },
  display: {
    dateInput: locale.dateFormat,
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: locale.dateFormat,
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: locale.localeId },

    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withViewTransitions()),
    provideHttpClient(withInterceptors([jwtInterceptor, errorInterceptor])),
    provideApollo(),
    provideAnimations(),

    // Translations
    TranslateModule.forRoot({
      defaultLanguage: 'sr',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }).providers!,
    // primng configuration providers
    BrowserModule,
    MessageService,
    {
      provide: APP_INITIALIZER,
      useFactory: (primeConfig: PrimeNGConfig) => () => {
        primeConfig.ripple = true;
        primeConfig.overlayOptions.appendTo = 'body';
        primeConfig.setTranslation({
          dayNames: locale.calendar.dayNames,
          dayNamesShort: locale.calendar.dayNamesShort,
          dayNamesMin: locale.calendar.dayNamesMin,
          monthNames: locale.calendar.monthNames,
          monthNamesShort: locale.calendar.monthNamesShort,
          today: locale.calendar.today,
          clear: locale.calendar.clear,
          dateFormat: locale.primeNgDateFormat,
          firstDayOfWeek: locale.calendar.firstDayOfWeek,
        });
      },
      deps: [PrimeNGConfig],
      multi: true,
    },

    // Social authentication providers
    importProvidersFrom(SocialLoginModule),
    ...GOOGLE_SIGN_IN_PROVIDER,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(), // Only enable in production
      registrationStrategy: 'registerWhenStable:30000'
    }),

    // App update service initialization
    {
      provide: APP_INITIALIZER,
      useFactory: (updateService: AppUpdateService) => () => {
        updateService.init();
      },
      deps: [AppUpdateService],
      multi: true,
    },

  ],
};
